import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Text, ResponsiveContainer } from 'recharts';
import { useSpring, animated } from 'react-spring';
import TextButton from '../../components/TextButton/TextButton';
import { NavArrowLeft, NavArrowRight } from '../MoodboardElementaryMobile/NavArrow';
import {
  getElementaryMoodColorFill,
  getElementaryMoodTextColor,
  parseSchoolJournalChartDataByPeriod,
  calculatePercentage,
  getEachMoodCheckInElementary,
  compareMoodData,
} from './moodboardUtilities';
import ChartCustomTooltip from './ChartCustomTooltip';
import { Icon } from '@iconify/react';
import faceHappy from '@iconify/icons-akar-icons/face-happy';
import faceNeutral from '@iconify/icons-akar-icons/face-neutral';
import faceSad from '@iconify/icons-akar-icons/face-sad';
import cx from 'classnames';
import styles from './ChartSegment.module.scss';
import { ReportingMixpanelEvent } from '../../../peekapak-types/ReportDataExchangeTypes';

interface Props {
  data: ReportingMixpanelEvent[];
  dataAvailabilityMessage: string;
  previousPeriodData: ReportingMixpanelEvent[];
  selectedPeriod: string;
  dateRange: { from: Date; to: Date };
  schoolName?: string;
  shiftPeriod: (direction: string) => void;
  shouldShowNavArrowLeft: () => boolean;
  shouldShowNavArrowRight: () => boolean;
}

const ChartSegmentElementary = ({
  data,
  dataAvailabilityMessage,
  previousPeriodData,
  selectedPeriod,
  dateRange,
  schoolName,
  shiftPeriod,
  shouldShowNavArrowLeft,
  shouldShowNavArrowRight,
}: Props): JSX.Element => {
  const barKeys = ['Excited', 'Happy', 'Calm', 'Nervous', 'Sad', 'Angry'];
  const { from, to } = dateRange;
  const dateRangeFrom = Date.parse(from.toString());
  const dateRangeTo = Date.parse(to.toString());

  const parsedData = parseSchoolJournalChartDataByPeriod(
    selectedPeriod,
    data,
    dateRangeFrom,
    dateRangeTo,
    'elementary',
  );

  const moodCheckInObj = getEachMoodCheckInElementary(data);
  const moodCheckInObjPrevious = getEachMoodCheckInElementary(previousPeriodData);

  const [isShow, setIsShow] = useState(true);

  const hideButtonSpringProps = useSpring({
    height: isShow ? 'fit-content' : '0',
    padding: isShow ? '2rem' : '0',
    opacity: isShow ? 1 : 0,
  });

  const showButtonSpringProps = useSpring({
    padding: isShow ? '5rem' : '2rem',
  });

  const handleShowHide = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsShow(!isShow);
    e.currentTarget.blur();
  };

  return (
    <section className={styles.chartSegment}>
      {isShow ? (
        <animated.div className={styles.barChartSectionContainer} style={hideButtonSpringProps}>
          <div className={styles.barChartSection}>
            <div className={styles.titleRow}>
              <h4 className={styles.chartMainHeader}>PK - 5 Check-ins</h4>
              <TextButton blue bold inline underlineHover onClick={handleShowHide}>
                Hide
              </TextButton>
            </div>
            <div className={styles.chartContainer}>
              <h4 className={styles.chartTitle}>PK-5 Overview {schoolName ? schoolName : ''}</h4>
              <h4 className={styles.dateRangeTitle}>
                {new Date(parsedData.from).toLocaleDateString()} - {new Date(parsedData.to).toLocaleDateString()}
              </h4>
              {dataAvailabilityMessage && <h4 className={styles.dataAvailabilityMsg}>{dataAvailabilityMessage}</h4>}
              <div className={styles.chart}>
                <div className={styles.icons}>
                  <Icon className={styles.happyFaceIcon} icon={faceHappy} />
                  <Icon className={styles.neutralFaceIcon} icon={faceNeutral} />
                  <Icon className={styles.sadFaceIcon} icon={faceSad} />
                </div>
                <ResponsiveContainer width='100%' height={500}>
                  <BarChart width={900} height={350} data={parsedData.data}>
                    <Tooltip content={<ChartCustomTooltip barKeys={barKeys} />} />
                    <XAxis
                      dataKey='name'
                      interval={0}
                      tick={<CustomXAxisTick />}
                      tickMargin={12}
                      tickLine={false}
                      height={45}
                    />
                    <YAxis />
                    {barKeys &&
                      barKeys
                        .reverse()
                        .map((key) => (
                          <Bar
                            dataKey={key}
                            key={key}
                            stackId='a'
                            fill={getElementaryMoodColorFill(key)}
                            barSize={50}
                          ></Bar>
                        ))}
                  </BarChart>
                </ResponsiveContainer>
                {shouldShowNavArrowLeft() && (
                  <div className={styles.navArrowLeft}>
                    <NavArrowLeft goToPrevious={() => shiftPeriod('previous')} />
                  </div>
                )}
                {shouldShowNavArrowRight() && (
                  <div className={styles.navArrowRight}>
                    <NavArrowRight goToNext={() => shiftPeriod('next')} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.percentageOverviewByPeriodContainer}>
            <div className={styles.thisPeriod}>
              <h4 className={styles.percentageOverviewByPeriodHeader}>This Period</h4>
              <div className={styles.percentageOverviewByPeriodContent}>
                {barKeys &&
                  barKeys.reverse().map((key) => {
                    const percentage = calculatePercentage(moodCheckInObj, barKeys, moodCheckInObj[key] || 0);
                    return (
                      <div
                        key={key}
                        className={styles.percentageOfMood}
                        style={{
                          backgroundColor: getElementaryMoodColorFill(key),
                        }}
                      >
                        <p
                          className={styles.percentageNumber}
                          style={{
                            color: getElementaryMoodTextColor(key),
                          }}
                        >
                          {`${percentage}%`}
                        </p>
                        <p
                          className={styles.moodName}
                          style={{
                            color: getElementaryMoodTextColor(key),
                          }}
                        >{`${key}`}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
            {selectedPeriod !== 'yearly' && shouldShowNavArrowLeft() && (
              <div className={styles.previousPeriod}>
                <h4 className={styles.percentageOverviewByPeriodHeader}>vs. Previous Period</h4>
                <div className={styles.percentageOverviewByPeriodContent}>
                  {barKeys &&
                    barKeys.map((key) => {
                      const percentage = compareMoodData(moodCheckInObj[key] || 0, moodCheckInObjPrevious[key] || 0);
                      return (
                        <div key={key} className={styles.comparisonPercentage}>
                          <p
                            className={cx(styles.percentageNumber, {
                              [styles.positive]: percentage > 0,
                              [styles.negative]: percentage < 0,
                            })}
                          >
                            {`${percentage}%`}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </animated.div>
      ) : (
        <></>
      )}
      {!isShow && (
        <animated.div className={styles.chartHiddenView} style={showButtonSpringProps}>
          <h4 className={styles.chartMainHeader}>PK - 5 Check-ins</h4>
          <TextButton blue bold inline underlineHover onClick={handleShowHide}>
            Show
          </TextButton>
        </animated.div>
      )}
    </section>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}

const CustomXAxisTick = ({ x, y, payload }: TickProps) => {
  if (payload && payload.value) {
    return (
      <Text fontSize={'14px'} width={75} x={x} y={y} textAnchor='middle' verticalAnchor='start'>
        {payload.value}
      </Text>
    );
  }
  return null;
};

export default ChartSegmentElementary;
